import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminUser, UserInput, UserKind } from '@/api/accessApi'
import { FormError } from '@/components/admin/AdminShared'
import { Field, NativeSelect } from '@/components/common/Field'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useRoles, useTeams } from '@/hooks/useAccess'
import { useCustomers } from '@/hooks/useDirectory'
import { assignableRoles, emptyUserForm, userPayload, userToForm, validateUser, type UserForm, type UserFormErrors } from '@/lib/accessForms'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

const KINDS: UserKind[] = ['AGENT', 'CUSTOMER']

/** Create (with an invite) or edit a user. `user` = edit; email and type are then fixed. */
export function UserDialog({
  open,
  user,
  pending,
  error,
  onOpenChange,
  onSubmit,
}: {
  open: boolean
  user: AdminUser | null
  pending: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onSubmit: (input: UserInput) => void
}) {
  const { t } = useTranslation()
  const editing = user !== null
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? t('access.users.editTitle', { name: `${user.firstName} ${user.lastName}` }) : t('access.users.createTitle')}</DialogTitle>
        </DialogHeader>
        {/* Keyed so the form starts fresh for each user it opens on. */}
        {open ? <UserFormBody key={user?.id ?? 'new'} user={user} pending={pending} error={error} onCancel={() => onOpenChange(false)} onSubmit={onSubmit} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function UserFormBody({
  user,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  user: AdminUser | null
  pending: boolean
  error: unknown
  onCancel: () => void
  onSubmit: (input: UserInput) => void
}) {
  const { t } = useTranslation()
  const { user: me } = useAuth()
  const editing = user !== null
  const [form, setForm] = useState<UserForm>(() => (user ? userToForm(user) : emptyUserForm()))
  const [errors, setErrors] = useState<UserFormErrors>({})
  const roles = useRoles()
  const teams = useTeams(form.kind === 'AGENT')
  const customers = useCustomers(form.kind === 'CUSTOMER')

  const allowed = assignableRoles(roles.data ?? [], form.kind, { isSuperAdmin: me?.isSuperAdmin ?? false, permissions: me?.permissions ?? [] })
  // Keep the current role visible when editing, even if you could not grant it yourself.
  const current = roles.data?.find((r) => r.id === user?.role?.id)
  const roleOptions = current && !allowed.includes(current) ? [current, ...allowed] : allowed
  const tenants = customers.data?.find((c) => c.id === form.customerId)?.tenants ?? []
  const set = <K extends keyof UserForm>(key: K, value: UserForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  const err = (key: keyof UserForm) => (errors[key] ? t(errors[key]!) : null)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const found = validateUser(form, editing)
    setErrors(found)
    if (Object.keys(found).length === 0) onSubmit(userPayload(form, user ?? undefined))
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-4" noValidate>
      {!editing ? (
        <fieldset className="col-span-2 flex flex-col gap-1.5">
          <legend className="pb-1.5 text-meta font-medium text-ink">{t('access.users.fields.kind')}</legend>
          <div role="radiogroup" className="inline-flex w-fit rounded-md border border-line-strong p-0.5">
            {KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={form.kind === k}
                onClick={() => setForm((f) => ({ ...f, kind: k, roleId: '', teamId: '', tier: '', customerId: '', tenantId: '', isSuperAdmin: false }))}
                className={cn(
                  'rounded-[5px] px-3 py-1.5 text-meta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  form.kind === k ? 'bg-accent font-medium text-accent-foreground' : 'text-ink-muted hover:text-ink',
                )}
              >
                {t(`access.kinds.${k}`)}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}

      <Field id="u-email" label={t('access.users.fields.email')} error={err('email')} help={editing ? undefined : t('access.users.fields.emailHelp')} className="col-span-2">
        <Input id="u-email" type="email" autoComplete="off" value={form.email} disabled={editing} onChange={(e) => set('email', e.target.value)} />
      </Field>
      <Field id="u-first" label={t('access.users.fields.firstName')} error={err('firstName')}>
        <Input id="u-first" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
      </Field>
      <Field id="u-last" label={t('access.users.fields.lastName')} error={err('lastName')}>
        <Input id="u-last" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
      </Field>
      <Field id="u-role" label={t('access.users.fields.role')} error={err('roleId')} help={t('access.users.fields.roleHelp')}>
        <NativeSelect id="u-role" value={form.roleId} onChange={(e) => set('roleId', e.target.value)}>
          <option value="">{t('access.users.fields.noRole')}</option>
          {roleOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.displayName}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field id="u-title" label={t('access.users.fields.title')} help={t('access.users.fields.titleHelp')}>
        <Input id="u-title" value={form.title} onChange={(e) => set('title', e.target.value)} />
      </Field>

      {form.kind === 'AGENT' ? (
        <>
          <Field id="u-team" label={t('access.users.fields.team')}>
            <NativeSelect id="u-team" value={form.teamId} onChange={(e) => set('teamId', e.target.value)}>
              <option value="">{t('access.users.fields.noTeam')}</option>
              {(teams.data ?? []).map((tm) => (
                <option key={tm.id} value={tm.id}>
                  {tm.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="u-tier" label={t('access.users.fields.tier')}>
            <NativeSelect id="u-tier" value={form.tier} onChange={(e) => set('tier', e.target.value)}>
              <option value="">{t('access.users.fields.noTier')}</option>
              {[1, 2, 3].map((n) => (
                <option key={n} value={String(n)}>
                  {t('user.tier', { tier: n })}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {me?.isSuperAdmin ? (
            <label className="col-span-2 flex items-center gap-2 text-meta text-ink">
              <input type="checkbox" className="size-4 accent-primary" checked={form.isSuperAdmin} onChange={(e) => set('isSuperAdmin', e.target.checked)} />
              {t('access.users.fields.superAdmin')}
            </label>
          ) : null}
        </>
      ) : (
        <>
          <Field id="u-customer" label={t('access.users.fields.customer')} error={err('customerId')}>
            <NativeSelect id="u-customer" value={form.customerId} disabled={editing} onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value, tenantId: '' }))}>
              <option value="">{t('access.users.fields.noCustomer')}</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="u-tenant" label={t('access.users.fields.tenant')}>
            <NativeSelect id="u-tenant" value={form.tenantId} disabled={!form.customerId} onChange={(e) => set('tenantId', e.target.value)}>
              <option value="">{t('access.users.fields.noTenant')}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>
                  {tn.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </>
      )}

      <FormError error={error} />
      <div className="col-span-2 flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending}>
          {editing ? t('access.users.save') : t('access.users.create')}
        </Button>
      </div>
    </form>
  )
}
